import { router } from "@inertiajs/react";
import { useCallback, useEffect } from "react";

const HISTORY_FLAG_KEY = "travily:has_in_app_history";

/**
 * Hook for "back" buttons that pop the in-app history stack — mobile-app-style.
 * Returns a callback that calls `window.history.back()` when the user has navigated
 * within the app this session, and falls back to `router.visit(fallbackHref)` when
 * they landed directly on the page (deep link, no in-app history).
 *
 * Detection works via a sessionStorage flag set by `useTrackInAppHistory`, mounted
 * once in `persistent-layout`. The first navigate event is skipped to avoid treating
 * the initial page load itself as user navigation.
 */
export function useNavigateBack(fallbackHref: string) {
  return useCallback(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(HISTORY_FLAG_KEY) === "true") {
      window.history.back();
    } else {
      router.visit(fallbackHref);
    }
  }, [fallbackHref]);
}

/** Mounts a single navigate listener that records whether the user has navigated in
 *  the app. Must be called once from a long-lived component (e.g. PersistentLayout)
 *  so the flag captures all in-app navigations during the session. */
export function useTrackInAppHistory() {
  useEffect(() => {
    let initialNavigateSeen = false;
    return router.on("navigate", () => {
      // Inertia may fire a 'navigate' event for the initial page load; skip it so
      // deep-link landings aren't treated as in-app history.
      if (!initialNavigateSeen) {
        initialNavigateSeen = true;
        return;
      }
      sessionStorage.setItem(HISTORY_FLAG_KEY, "true");
    });
  }, []);
}
